import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { ApprovalDecisionDialog } from './ApprovalDecisionDialog';
import { ApprovalExecutionDialog } from './ApprovalExecutionDialog';
import { toApprovalPageError } from './ApprovalListPage';
import { approvalsRepositoryToken } from './repository';

export function ApprovalDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { approvalId = '' } = useParams();
  const repository = useRepository(approvalsRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [decision, setDecision] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [executeOpen, setExecuteOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.get(approvalId, signal),
    [approvalId, refreshKey, repository],
    () => false,
  );
  const reload = () => setRefreshKey((value) => value + 1);
  const principal = auth.status === 'authenticated' ? auth.principal : null;
  const permissions = new Set(principal?.permissions ?? []);
  const canDecide = permissions.has('APPROVAL_DECIDE');
  const isMaker =
    principal !== null &&
    resource.data !== null &&
    resource.data.requestedBy === principal.actor.id;
  const status = resource.data?.status ?? null;
  // Approve/Reject are only meaningful while the request is still pending a decision; Execute is only
  // meaningful once it has been approved and not yet executed. Terminal states (REJECTED, EXPIRED,
  // EXECUTED) never show live action buttons — the server would always reject the conflicting action.
  const canShowDecide = canDecide && status === 'PENDING';
  const canShowExecute = canDecide && status === 'APPROVED';

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toApprovalPageError(resource.error, t('approvals.errors.loadDetail'))}
        onRetry={reload}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              backTo={paths.approvals}
              dataSource={dataSource}
              subtitle={`${resource.data.actionType} · ${resource.data.status}`}
              title={resource.data.id}
            />
            {isMaker ? (
              <Alert severity="warning">{t('approvals.selfApprovalBlocked')}</Alert>
            ) : null}
            {canShowDecide || canShowExecute ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {canShowDecide ? (
                  <>
                    <Button
                      disabled={isMaker}
                      onClick={() => setDecision('APPROVE')}
                      variant="contained"
                    >
                      {t('approvals.actions.approve')}
                    </Button>
                    <Button
                      color="warning"
                      onClick={() => setDecision('REJECT')}
                      variant="outlined"
                    >
                      {t('approvals.actions.reject')}
                    </Button>
                  </>
                ) : null}
                {canShowExecute ? (
                  <Button onClick={() => setExecuteOpen(true)} variant="outlined">
                    {t('approvals.actions.execute')}
                  </Button>
                ) : null}
              </Stack>
            ) : (
              canDecide && (
                <Typography color="text.secondary">
                  {t('approvals.actions.noActionAvailable', { status: resource.data.status })}
                </Typography>
              )
            )}
            <MetadataList
              items={[
                { label: t('approvals.fields.partnerId'), value: resource.data.partnerId },
                { label: t('approvals.fields.actionType'), value: resource.data.actionType },
                { label: t('approvals.fields.entityType'), value: resource.data.entityType },
                {
                  label: t('approvals.fields.entityId'),
                  value: resource.data.entityId ?? t('common.notAvailable'),
                },
                { label: t('approvals.fields.reason'), value: resource.data.reason },
                {
                  label: t('approvals.fields.evidenceReference'),
                  value: resource.data.evidenceReference ?? t('common.notAvailable'),
                },
                { label: t('approvals.fields.status'), value: resource.data.status },
                { label: t('approvals.fields.requestedBy'), value: resource.data.requestedBy },
                {
                  label: t('approvals.fields.requestedAt'),
                  value: withOffset(resource.data.requestedAt, language),
                },
                {
                  label: t('approvals.fields.expiresAt'),
                  value: withOffset(resource.data.expiresAt, language),
                },
                {
                  label: t('approvals.fields.decidedBy'),
                  value: resource.data.decidedBy ?? t('common.notAvailable'),
                },
                {
                  label: t('approvals.fields.decidedAt'),
                  value: nullableTime(resource.data.decidedAt, language, t('common.notAvailable')),
                },
                {
                  label: t('approvals.fields.decisionReason'),
                  value: resource.data.decisionReason ?? t('common.notAvailable'),
                },
                {
                  label: t('approvals.fields.executedAt'),
                  value: nullableTime(resource.data.executedAt, language, t('common.notAvailable')),
                },
                {
                  label: t('approvals.fields.executionReference'),
                  value: resource.data.executionReference ?? t('common.notAvailable'),
                },
              ]}
            />
            <ApprovalDecisionDialog
              approval={resource.data}
              dataSource={dataSource}
              decision={decision}
              onClose={(completed) => {
                setDecision(null);
                if (completed) reload();
              }}
            />
            <ApprovalExecutionDialog
              approval={resource.data}
              dataSource={dataSource}
              onClose={(completed) => {
                setExecuteOpen(false);
                if (completed) reload();
              }}
              onStale={reload}
              open={executeOpen}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function withOffset(value: string, language: TimeLanguage) {
  const time = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${time.display} (${time.originalOffset})`;
}

function nullableTime(value: string | null, language: TimeLanguage, fallback: string) {
  return value === null ? fallback : withOffset(value, language);
}
